import React, { useEffect, useState } from 'react';
import { MediaHelpers } from '@wajeez-web/core/api';
import { Skeleton } from 'antd';
import { ImSpinner8 } from 'react-icons/im';

import {
  BaseComponentProps,
  useComponentData,
  useSkeletonHelper,
} from '../helpers/contentHooks';
import { ClickableComponentLayout } from '../layouts/ClickableComponentLayout';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

export interface RepeatedAnyFullWidthParameters {
  title: string | null;
  seeAllTitle: string | null;
  description: string | null;
}

interface RepeatedAnyFullWidthProps extends BaseComponentProps {
  setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
}

export function RepeatedAnyFullWidth({
  component,
  context,
  setIsActive,
  isLastSibling = false,
}: RepeatedAnyFullWidthProps) {
  const [pageNumber, setPageNumber] = useState(1);

  const { getParameterValue, contents, contentsRequestStatus, meta } =
    useComponentData<RepeatedAnyFullWidthParameters>(
      component,
      context,
      pageNumber
    );

  const { lastElementRef } = useInfiniteScroll(
    meta?.hasNextPage,
    setPageNumber
  );

  const { isActive } = useSkeletonHelper(
    contentsRequestStatus,
    contents?.length ? true : false
  );

  const seeAllTitle = getParameterValue('seeAllTitle');
  const title = getParameterValue('title');

  useEffect(() => {
    setIsActive(isActive);
  }, [isActive, setIsActive]);

  return (
    <div className="pb-[51px] sm:pb-0">
      <div className="flex justify-between px-4 space-y-[17px]">
        <div className="font-bold text-[20px]">{title}</div>
        {seeAllTitle && <div className=" text-[17px]">{seeAllTitle}</div>}
      </div>
      <div className="px-4">
        <div
          className={`w-full ${
            !isLastSibling && 'border-b-[0.5px]'
          } border-[#C6C6C8] dark:border-dark-divider`}
        >
          {contents?.map((content, i) => (
            <RepeatedAnyFullWidthItem
              key={content.id}
              component={{ ...component?.itemTemplate, uniqueId: i }}
              context={content}
              isLastSibling={contents.length - 1 === i}
              ref={lastElementRef}
            />
          ))}
          {contentsRequestStatus === 'loading' && contents?.length && (
            <div className="sm:hidden w-full justify-center items-center text-[24px] text-primary dark:text-primary-dark">
              <ImSpinner8 className="animate-spin mx-auto" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const RepeatedAnyFullWidthSkeleton = ({
  isLastSibling,
  isActive = true,
}: {
  isLastSibling: boolean | 0 | undefined;
  isActive?: boolean;
}) => {
  return (
    <div>
      <div className="flex justify-between px-4">
        <Skeleton.Button
          active={isActive}
          shape="square"
          className="!w-[200px] !h-[25px] dark:bg-dark-medium"
        />
      </div>
      <div className="px-4">
        <div
          className={`w-full ${
            !isLastSibling && 'border-b-[0.5px]'
          } border-[#C6C6C8] dark:border-dark-divider`}
        >
          {Array.from({ length: 10 }).map((_, i) => {
            return (
              <div
                key={i}
                className={`w-full py-2 flex justify-start items-center ${
                  i !== 9 && 'border-b-[0.5px]'
                } border-[#C6C6C8] dark:border-dark-divider`}
              >
                <Skeleton.Node
                  active={isActive}
                  className="!w-[90px] !h-[90px] dark:bg-dark-medium"
                >
                  <div />
                </Skeleton.Node>
                <Skeleton.Button
                  active={isActive}
                  shape="square"
                  className="mr-2 !w-[200px] !h-[25px] dark:bg-dark-medium"
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export interface RepeatedAnyFullWidthItemParameters {
  image: string | null;
  description: string | null;
}

const RepeatedAnyFullWidthItem = React.forwardRef<
  HTMLDivElement,
  BaseComponentProps
>(({ component, context, isLastSibling }, ref) => {
  const { getParameterValue, content } =
    useComponentData<RepeatedAnyFullWidthItemParameters>(component, context);

  const image = getParameterValue('image');

  return (
    <ClickableComponentLayout content={content}>
      <div
        ref={ref}
        className={`relative w-full pt-[16px] pb-[16px] flex justify-start items-center bg-white dark:bg-black ${
          !isLastSibling && 'border-b-[0.5px]'
        } border-[#C6C6C8] dark:border-dark-divider`}
      >
        <div className="w-[90px] h-[90px] flex-none rounded-[8px]">
          <img
            src={MediaHelpers.getPublicMediaById(image)}
            alt="repeated any full width item"
            className="w-full h-full rounded-[8px] object-cover"
          />
        </div>
        <div className="mr-[8px] flex flex-col justify-center items-start">
          <h1 className="text-[17px] font-normal text-right">
            {content?.title}
          </h1>
          <h2 className="mt-[3px] text-[12px] font-normal text-right text-[rgba(60,_60,_67,_0.6)] dark:text-dark-light">
            {content?.authors?.toString().replaceAll(',', ' و ')}
          </h2>
          {getParameterValue('description') && (
            <h2 className="mt-[3px] text-[12px] font-normal text-right text-[rgba(60,_60,_67,_0.6)] dark:text-dark-light">
              {getParameterValue('description')?.slice(0, 80) +
                ((getParameterValue('description')?.length || 0) > 80
                  ? '...'
                  : '')}
            </h2>
          )}
        </div>
      </div>
    </ClickableComponentLayout>
  );
});
